import React, { useState } from 'react';
import { SearchInput } from '@/components/ui/search-input';
import {
	Filter,
	ChevronLeft,
	ChevronRight,
	Package,
	DollarSign,
	Calendar,
	User
} from 'lucide-react';

interface Order {
	id: number;
	orderNumber: string;
	customer: string;
	customerEmail: string;
	total: number;
	status: 'Pending' | 'Processing' | 'Shipped' | 'Delivered' | 'Cancelled';
	items: number;
	createdAt: string;
}

const mockOrders: Order[] = [
	{
		id: 1,
		orderNumber: 'ORD-2024-001',
		customer: 'John Doe',
		customerEmail: 'john.doe@example.com',
		total: 2499,
		status: 'Delivered',
		items: 1,
		createdAt: '2024-01-20'
	},
	{
		id: 2,
		orderNumber: 'ORD-2024-002',
		customer: 'Jane Smith',
		customerEmail: 'jane.smith@example.com',
		total: 1448,
		status: 'Shipped',
		items: 2,
		createdAt: '2024-01-19'
	},
	{
		id: 3,
		orderNumber: 'ORD-2024-003',
		customer: 'Bob Johnson',
		customerEmail: 'bob.johnson@example.com',
		total: 599,
		status: 'Processing',
		items: 1,
		createdAt: '2024-01-18'
	},
	{
		id: 4,
		orderNumber: 'ORD-2024-004',
		customer: 'Alice Brown',
		customerEmail: 'alice.brown@example.com',
		total: 1798,
		status: 'Pending',
		items: 3,
		createdAt: '2024-01-17'
	},
	{
		id: 5,
		orderNumber: 'ORD-2024-005',
		customer: 'Charlie Wilson',
		customerEmail: 'charlie.wilson@example.com',
		total: 249,
		status: 'Cancelled',
		items: 1,
		createdAt: '2024-01-16'
	}
];

export const OrderList: React.FC = () => {
	const [orders] = useState<Order[]>(mockOrders);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedStatus, setSelectedStatus] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(5);

	const statuses = [
		'Pending',
		'Processing',
		'Shipped',
		'Delivered',
		'Cancelled'
	];

	const filteredOrders = orders.filter(order => {
		const matchesSearch =
			order.orderNumber
				.toLowerCase()
				.includes(searchTerm.toLowerCase()) ||
			order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
			order.customerEmail
				.toLowerCase()
				.includes(searchTerm.toLowerCase());
		const matchesStatus =
			!selectedStatus || order.status === selectedStatus;
		return matchesSearch && matchesStatus;
	});

	const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const paginatedOrders = filteredOrders.slice(
		startIndex,
		startIndex + itemsPerPage
	);

	const getStatusBadge = (status: Order['status']) => {
		switch (status) {
			case 'Delivered':
				return 'badge-success';
			case 'Shipped':
				return 'badge-info';
			case 'Processing':
				return 'bg-blue-100 text-blue-800';
			case 'Pending':
				return 'badge-warning';
			case 'Cancelled':
				return 'badge-danger';
			default:
				return 'badge-info';
		}
	};

	return (
		<div className="space-y-6">
			{/* Page Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Orders</h1>
					<p className="text-gray-600 mt-1">
						Track and manage customer orders
					</p>
				</div>
			</div>

			{/* Stats Overview */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
				<div className="kt-card">
					<div className="kt-card-body text-center">
						<Package className="w-8 h-8 text-blue-600 mx-auto mb-2" />
						<p className="text-2xl font-bold text-gray-900">
							{orders.length}
						</p>
						<p className="text-sm text-gray-600">Total Orders</p>
					</div>
				</div>
				<div className="kt-card">
					<div className="kt-card-body text-center">
						<DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
						<p className="text-2xl font-bold text-gray-900">
							$
							{orders
								.reduce((sum, order) => sum + order.total, 0)
								.toLocaleString()}
						</p>
						<p className="text-sm text-gray-600">Total Revenue</p>
					</div>
				</div>
				<div className="kt-card">
					<div className="kt-card-body text-center">
						<Package className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
						<p className="text-2xl font-bold text-gray-900">
							{
								orders.filter(o =>
									['Pending', 'Processing'].includes(o.status)
								).length
							}
						</p>
						<p className="text-sm text-gray-600">Processing</p>
					</div>
				</div>
				<div className="kt-card">
					<div className="kt-card-body text-center">
						<Package className="w-8 h-8 text-purple-600 mx-auto mb-2" />
						<p className="text-2xl font-bold text-gray-900">
							{
								orders.filter(o => o.status === 'Delivered')
									.length
							}
						</p>
						<p className="text-sm text-gray-600">Delivered</p>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="kt-card">
				<div className="kt-card-body">
					<div className="flex flex-col sm:flex-row gap-4">
					<div className="flex-1">
						<SearchInput
							placeholder="Search orders..."
							value={searchTerm}
							onChange={e => setSearchTerm(e.target.value)}
						/>
					</div>
						<div className="sm:w-48">
							<div className="relative">
								<Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
								<select
									value={selectedStatus}
									onChange={e =>
										setSelectedStatus(e.target.value)
									}
									className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
									title="Filter by status"
								>
									<option value="">All Status</option>
									{statuses.map(status => (
										<option key={status} value={status}>
											{status}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Orders Table */}
			<div className="kt-card">
				<div className="kt-card-body">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead>
								<tr className="border-b border-gray-200">
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Order
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Customer
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Items
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Total
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Status
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Date
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{paginatedOrders.map(order => (
									<tr
										key={order.id}
										className="hover:bg-gray-50"
									>
										<td className="py-4 px-4">
											<div>
												<p className="text-sm font-medium text-gray-900">
													{order.orderNumber}
												</p>
												<p className="text-xs text-gray-500">
													Order #{order.id}
												</p>
											</div>
										</td>
										<td className="py-4 px-4">
											<div className="flex items-center">
												<div className="w-8 h-8 bg-gray-200 rounded-full mr-3 flex items-center justify-center">
													<User className="w-4 h-4 text-gray-600" />
												</div>
												<div>
													<p className="text-sm font-medium text-gray-900">
														{order.customer}
													</p>
													<p className="text-xs text-gray-500">
														{order.customerEmail}
													</p>
												</div>
											</div>
										</td>
										<td className="py-4 px-4">
											<span className="text-sm text-gray-900">
												{order.items} item
												{order.items !== 1 ? 's' : ''}
											</span>
										</td>
										<td className="py-4 px-4">
											<span className="text-sm font-semibold text-gray-900">
												${order.total.toLocaleString()}
											</span>
										</td>
										<td className="py-4 px-4">
											<span
												className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
													order.status
												)}`}
											>
												{order.status}
											</span>
										</td>
										<td className="py-4 px-4">
											<div className="flex items-center text-sm text-gray-600">
												<Calendar className="w-3 h-3 mr-2 text-gray-400" />
												{order.createdAt}
											</div>
										</td>
										<td className="py-4 px-4">
											{/* Actions removed */}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					<div className="flex items-center justify-between mt-6">
						<div className="text-sm text-gray-500">
							Showing {startIndex + 1} to{' '}
							{Math.min(
								startIndex + itemsPerPage,
								filteredOrders.length
							)}{' '}
							of {filteredOrders.length} results
						</div>
						<div className="flex items-center space-x-2">
							<button
								onClick={() =>
									setCurrentPage(prev =>
										Math.max(prev - 1, 1)
									)
								}
								disabled={currentPage === 1}
								className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
								title="Previous page"
							>
								<ChevronLeft className="w-4 h-4" />
							</button>

							{Array.from(
								{ length: totalPages },
								(_, i) => i + 1
							).map(page => (
								<button
									key={page}
									onClick={() => setCurrentPage(page)}
									className={`px-3 py-1 rounded ${
										currentPage === page
											? 'bg-blue-600 text-white'
											: 'text-gray-600 hover:bg-gray-100'
									}`}
								>
									{page}
								</button>
							))}

							<button
								onClick={() =>
									setCurrentPage(prev =>
										Math.min(prev + 1, totalPages)
									)
								}
								disabled={currentPage === totalPages}
								className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
								title="Next page"
							>
								<ChevronRight className="w-4 h-4" />
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
